import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CartService } from '../cart/cart.service';
import { ShopSelectionStore } from '../../state/shop-selection.store';
import { ZeroPriceType } from '../../types/enums';
import { Product } from '../../../shared/models/product.model';
import { Resident } from '../../../shared/models/resident.model';
import { InformationModalComponent } from '../../../shared/components/information-modal/information-modal.component';
import { SnackbarService } from '../utils/snackbar.service';
import {
  ZERO_PRICE_LIMIT_REACHED_MESSAGE,
  effectiveZeroPriceType,
  zeroPriceLimitFor,
} from './zero-price.util';

type ZeroPriceScope = {
  product: Product | null;
  resident: Resident | null;
};

type ZeroPriceGuardParams = ZeroPriceScope & {
  currentValue: number;
  nextValue: number;
};

@Injectable({
  providedIn: 'root',
})
export class ZeroPriceGuardService {
  /**
   * Which area the customer is shopping in. Resolved here rather than passed in by every
   * caller: it is the same selection for all of them, and only this service acts on it.
   * `ShopSelectionStore.department$` is a BehaviorSubject and the service is root-provided for the
   * app's lifetime, so there is nothing to tear down.
   */
  private isB2BDepartment = false;

  constructor(
    private cartService: CartService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    shopSelectionStore: ShopSelectionStore
  ) {
    shopSelectionStore.department$.subscribe(department => {
      this.isB2BDepartment = department?.isB2B ?? false;
    });
  }

  canIncrease({
    product,
    resident,
    currentValue,
    nextValue,
  }: ZeroPriceGuardParams): boolean {
    const zeroPriceType = effectiveZeroPriceType(product);

    if (!product || zeroPriceType === ZeroPriceType.None) {
      return true;
    }

    const isB2BDepartment = this.isB2BDepartment;

    if (zeroPriceType === ZeroPriceType.B2B && !isB2BDepartment) {
      this.openZeroPriceDialog('B2C', zeroPriceType);
      return false;
    }

    if (zeroPriceType === ZeroPriceType.B2C && isB2BDepartment) {
      this.openZeroPriceDialog('B2B', zeroPriceType);
      return false;
    }

    if (
      !isB2BDepartment &&
      currentValue === 0 &&
      (zeroPriceType === ZeroPriceType.B2C ||
        zeroPriceType === ZeroPriceType.B2B_B2C)
    ) {
      const residentCart = this.cartService.cart.getResidentCart(resident?.id);
      const cartTotal = residentCart?.total ?? 0;
      if (cartTotal <= 0) {
        this.openZeroPriceDialog('B2C', zeroPriceType);
        return false;
      }
    }

    const maxQuantity = this.maxQuantityFor({ product, resident });

    if (maxQuantity !== null && nextValue > maxQuantity) {
      this.snackbarService.error(ZERO_PRICE_LIMIT_REACHED_MESSAGE);
      return false;
    }

    return true;
  }

  /**
   * The highest quantity the counter may reach for this article, or `null` when unlimited.
   *
   * The limit is per cart and never cumulative across orders. In B2C that cart is the
   * resident's, so every resident gets the full allowance. In B2B the whole department cart
   * is one order, so whatever other resident carts already hold is taken off the allowance.
   */
  maxQuantityFor({ product, resident }: ZeroPriceScope): number | null {
    const limit = zeroPriceLimitFor(product, this.isB2BDepartment);

    if (limit === null) {
      return null;
    }
    if (!this.isB2BDepartment) {
      return limit;
    }

    return Math.max(0, limit - this.quantityInOtherCarts(product, resident));
  }

  private quantityInOtherCarts(
    product: Product | null,
    resident: Resident | null
  ): number {
    const residentCarts = this.cartService.cart?.residentCarts ?? [];

    return residentCarts
      .filter(residentCart => residentCart?.residentId !== resident?.id)
      .reduce(
        (total, residentCart) =>
          total +
          ((residentCart?.products ?? []).find(p => p?.id === product?.id)
            ?.quantity ?? 0),
        0
      );
  }

  private openZeroPriceDialog(
    departmentType: 'B2B' | 'B2C',
    productType: ZeroPriceType
  ): void {
    if (departmentType === 'B2C' && productType === ZeroPriceType.B2B_B2C) {
      this.dialog.open(InformationModalComponent, {
        data: {
          title:
            'Gratis-Produkte können nicht <br> in eine leere Tüte gelegt werden.',
          message:
            'Füge zunächst ein anderes <b>Produkt</b> zur Bewohner-Tüte hinzu oder bestelle das Gratis-Produkt als <b>Hausbestellung (B2B)</b> für die gesamte Einrichtung.',
        },
        width: '632px',
        height: '222px',
        disableClose: true,
      });
    } else if (departmentType === 'B2C' && productType === ZeroPriceType.B2C) {
      this.dialog.open(InformationModalComponent, {
        data: {
          title:
            'Gratis-Produkte können nicht in eine leere Tüte gelegt werden.',
          message:
            'Um das Gratis-Produkt zu bestellen, füge zunächst ein anderes <b>Produkt</b> zur Bewohner-Tüte hinzu.',
        },
        width: '632px',
        height: '225px',
        disableClose: true,
      });
    } else if (departmentType === 'B2C' && productType === ZeroPriceType.B2B) {
      this.dialog.open(InformationModalComponent, {
        data: {
          title:
            'Dieses Gratis-Produkt kann nicht für einzelne Bewohner:innen bestellt werden.',
          message:
            'Um das Gratis-Produkt zu bestellen, nutze die <b>Hausbestellung (B2B)</b>.',
        },
        width: '632px',
        height: '200px',
        disableClose: true,
      });
    } else {
      this.dialog.open(InformationModalComponent, {
        data: {
          title:
            'Dieses Gratis-Produk kann nicht als Hausbestellung (B2B) bestellt werden.',
          message:
            'Um dieses Gratis-Produkt bestellen zu können, lege es einzelnen Bewohner:innen in die Tüte.',
        },
        width: '632px',
        height: '225px',
        disableClose: true,
      });
    }
  }
}
